import { API_ENDPOINTS, httpClientInstance } from "@/api"
import { authService } from "@/services/auth.service"
import { AccountStatus } from "@/types/auth.types"
import { create } from "zustand"

export interface PersonalInfoData {
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  phone?: string
}

export interface BusinessInfoData {
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  phone?: string
  company_name?: string
}

export interface ProfileUpdateData {
  first_name?: string
  last_name?: string
  middle_name?: string | null
  username?: string
  phone?: string
  website?: string | null
  account_type?: "personal" | "business"
  personal_info?: PersonalInfoData
  business_info?: BusinessInfoData
}

interface UserProfileState {
  // State
  accountStatus: AccountStatus | null
  organizationCostThreshold: number | null
  isLoading: boolean
  error: string | null

  // Actions
  fetchAccountStatus: () => Promise<void>
  fetchOrganizationCostThreshold: () => Promise<void>
  updateOrganizationCostThreshold: (value: number) => Promise<void>
  updateProfile: (data: ProfileUpdateData) => Promise<void>
  changePlan: (tierId: string) => Promise<void>
  clearAccountStatus: () => void
  clearError: () => void
}

/**
 * User Profile Store
 * Manages user account data from /api/v2/account/me/ endpoint
 */
export const useUserProfileStore = create<UserProfileState>((set) => ({
  // Initial state
  accountStatus: null,
  organizationCostThreshold: null,
  isLoading: false,
  error: null,

  // Fetch account status from API
  fetchAccountStatus: async () => {
    set({ isLoading: true, error: null })

    try {
      const status = await authService.fetchAccountStatus()
      set({ accountStatus: status, isLoading: false, error: null })
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || "Failed to fetch account status"
      set({ error: errorMessage, isLoading: false })
      console.error("Error fetching account status:", error)
    }
  },

  // Fetch organization cost threshold from API
  fetchOrganizationCostThreshold: async () => {
    try {
      const data = await httpClientInstance.get<{ organization_cost_threshold: number }>(
        API_ENDPOINTS.account.organizationCostThreshold
      )
      set({ organizationCostThreshold: data.organization_cost_threshold })
    } catch (error: any) {
      console.error("Failed to fetch organization cost threshold:", error)
      throw error
    }
  },

  // Update organization cost threshold
  updateOrganizationCostThreshold: async (value: number) => {
    const data = await httpClientInstance.put<{ organization_cost_threshold: number }>(
      API_ENDPOINTS.account.organizationCostThreshold,
      { organization_cost_threshold: value }
    )
    set({ organizationCostThreshold: data.organization_cost_threshold })
  },

  // Update editable profile fields
  updateProfile: async (data: ProfileUpdateData) => {
    set({ isLoading: true, error: null })
    try {
      const updated = await httpClientInstance.patch<AccountStatus>(API_ENDPOINTS.account.update, data)
      set((state) => ({
        accountStatus: state.accountStatus ? { ...state.accountStatus, ...updated } : state.accountStatus,
        isLoading: false,
      }))
    } catch (error: any) {
      const data = error.response?.data
      let errorMessage: string
      if (data?.detail) {
        errorMessage = data.detail
      } else if (data && typeof data === "object") {
        errorMessage = Object.entries(data)
          .flatMap(([field, msgs]) =>
            Array.isArray(msgs)
              ? (msgs as string[]).map((m) => `${field}: ${m}`)
              : [`${field}: ${msgs}`]
          )
          .join("\n")
      } else {
        errorMessage = error.message || "Failed to update profile"
      }
      set({ error: errorMessage, isLoading: false })
      throw new Error(errorMessage)
    }
  },

  // Change subscription tier
  changePlan: async (tierId: string) => {
    set({ isLoading: true, error: null })
    try {
      await httpClientInstance.post(API_ENDPOINTS.account.changePlan, { tier: tierId })
      set((state) => ({
        accountStatus: state.accountStatus
          ? { ...state.accountStatus, cloudspry_tier: tierId as AccountStatus["cloudspry_tier"] }
          : state.accountStatus,
        isLoading: false,
      }))
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || "Failed to change plan"
      set({ error: errorMessage, isLoading: false })
      throw new Error(errorMessage)
    }
  },

  // Clear account status data
  clearAccountStatus: () => {
    set({ accountStatus: null, organizationCostThreshold: null, isLoading: false, error: null })
  },

  // Clear error only
  clearError: () => {
    set({ error: null })
  },
}))
